'use client';

import { useEffect, useMemo } from 'react';

import {
  useChatRealtime,
  useConversation,
  useMarkAsRead,
} from '@kit/chat/hooks';
import { isBalleeUser } from '@kit/chat/lib';
import { Button } from '@kit/ui/button';
import { Spinner } from '@kit/ui/spinner';
import { Trans } from '@kit/ui/trans';

import { MessageComposer } from '~/home/(user)/inbox/_components/message-composer';
import { MessageList } from '~/home/(user)/inbox/_components/message-list';

interface FloatingChatViewProps {
  conversationId: string;
  onBack: () => void;
  /**
   * Whether the composer offers the structured booking-inquiry form.
   * Item-anchored overrides (marketplace seller DMs) disable it — the
   * booking form is a dancer-booking instrument, not a listing one.
   */
  showBookingInquiry?: boolean;
}

export function FloatingChatView({
  conversationId,
  onBack,
  showBookingInquiry = true,
}: FloatingChatViewProps) {
  const {
    data: conversation,
    isLoading,
    error,
  } = useConversation(conversationId);
  const { markAsRead } = useMarkAsRead();
  const { typingUsers, activeTools } = useChatRealtime({ conversationId });

  const isBalleeConversation = useMemo(() => {
    if (!conversation) return false;
    return conversation.participants.some((p) => isBalleeUser(p.user_id));
  }, [conversation]);

  // Mark as read when opening conversation
  useEffect(() => {
    if (conversationId) {
      markAsRead(conversationId).catch(() => {
        // Silently ignore errors
      });
    }
  }, [conversationId, markAsRead]);

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (error || !conversation) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-2 p-4 text-center">
        <p className="text-destructive text-sm">
          <Trans i18nKey="chat:floatingChat.loadFailed" />
        </p>
        <Button variant="outline" size="sm" onClick={onBack}>
          <Trans i18nKey="chat:floatingChat.tryAgain" />
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <MessageList
        conversationId={conversationId}
        typingUsers={typingUsers}
        activeTools={activeTools}
      />

      <MessageComposer
        conversationId={conversationId}
        isBalleeConversation={isBalleeConversation}
        pageContext={null}
        showBookingInquiry={showBookingInquiry}
      />
    </div>
  );
}
